/**
 * Executor de agendamento — consome um agendamento + handle de diretório
 * + (opcionalmente) snapshot do painel registry e produz arquivos no disco.
 *
 * Estratégia:
 *  - PDF/Excel/CSV: precisa do payload (resumo + secoes). Tenta primeiro
 *    o snapshot local (página visitada na sessão), depois cai em iframe
 *    oculto que carrega a rota e lê o snapshot publicado por ela.
 *  - PNG/JPG: precisa de DOM real — SEMPRE via iframe oculto, para não
 *    mutar a aba em que o usuário está trabalhando (ver executarFormatoImagem).
 */

import {
  gerarBlobPDF,
  gerarBlobExcel,
  gerarBlobsCSV,
  combineCanvasesVertical,
  motivoImagemInviavel,
  slugifyFilename,
  timestampForFilename,
  marcaDaExportacao,
} from "../exportacao";
import {
  obterSnapshot,
  obterLabel,
  obterRota,
  snapshotEstaFresco,
} from "../painelRegistry";
import { abrirPainelEmIframe } from "./iframeCapture";

/**
 * Filtra colunas/resumo `type:currency` se `ocultarPrecos` está ativo.
 */
function filtrarPrecos({ resumo = [], secoes = [] }) {
  return {
    resumo: resumo.filter((r) => r?.type !== "currency"),
    secoes: secoes.map((s) => ({
      ...s,
      columns: (s.columns || []).filter((c) => c?.type !== "currency"),
    })),
  };
}

async function escreverArquivo(handle, filename, blob) {
  let nomeFinal = filename;
  let counter = 1;
  let existe = await arquivoExiste(handle, nomeFinal);
  const dotIdx = filename.lastIndexOf(".");
  const base = dotIdx > 0 ? filename.slice(0, dotIdx) : filename;
  const ext = dotIdx > 0 ? filename.slice(dotIdx) : "";
  while (existe) {
    nomeFinal = `${base}_${counter}${ext}`;
    counter++;
    if (counter > 99) {
      throw new Error("Muitos arquivos com o mesmo nome — limpar pasta.");
    }
    existe = await arquivoExiste(handle, nomeFinal);
  }

  const fileHandle = await handle.getFileHandle(nomeFinal, { create: true });
  const writable = await fileHandle.createWritable();
  await writable.write(blob);
  await writable.close();
  return nomeFinal;
}

async function arquivoExiste(handle, name) {
  try {
    await handle.getFileHandle(name, { create: false });
    return true;
  } catch {
    return false;
  }
}

async function resolverHandleDestino(rootHandle, subpasta) {
  if (!subpasta) return rootHandle;
  const limpa = String(subpasta).trim().replace(/[\\/:*?"<>|]/g, "_");
  // A File System Access API rejeita "." e ".." com TypeError — e uma
  // exceção aqui derrubava o lote inteiro (ver executarFormato*).
  if (!limpa || limpa === "." || limpa === "..") return rootHandle;
  return rootHandle.getDirectoryHandle(limpa, { create: true });
}

async function canvasToBlob(canvas, mime, quality) {
  return new Promise((resolve, reject) => {
    canvas.toBlob(
      (blob) => (blob ? resolve(blob) : reject(new Error("toBlob retornou null"))),
      mime,
      quality
    );
  });
}

/**
 * Obtém o snapshot do painel — tenta primeiro do registry local; se não
 * achar, monta iframe e lê de `iframe.contentWindow.__pharuerpSnapshots`.
 *
 * @returns {Promise<{snap, aviso}|null>}
 */
async function resolverSnapshot(painelId, { warmupRequest, cacheDoLote } = {}) {
  // Um lote com 3 formatos do mesmo painel abria 3 iframes — 3 boots completos
  // do app para ler exatamente o mesmo snapshot (medido: 27s para pdf+xlsx+csv
  // de um painel fechado). O cache vive só enquanto o lote roda, então não há
  // risco de servir dado velho numa execução posterior.
  const emCache = cacheDoLote?.get(painelId);
  if (emCache) return emCache;
  // Snapshot fresco (< 10min): usa direto. Stale: prefere recarregar via
  // iframe — senão o agendamento das 18h exporta os dados de quando o
  // usuário visitou a página de manhã, sem nenhum aviso. O stale só é
  // usado como último recurso, com aviso explícito no histórico.
  const local = obterSnapshot(painelId);
  if (local && snapshotEstaFresco(local)) return { snap: local, aviso: null };

  const rota = obterRota(painelId);
  if (!rota) return null;

  // Warm-up proativo antes do iframe (mesma razão do JPG: cold start +
  // race de refresh). PDF/Excel/CSV só caem aqui quando não há snapshot
  // local, mas o iframe sofre o mesmo risco de redirect /login.
  if (warmupRequest) {
    try {
      await warmupRequest("/api/me", { silentError: true });
    } catch (warmErr) {
      throw new Error(
        `Sessão inválida ao tentar capturar "${obterLabel(painelId)}" ` +
          `via iframe (recarregue a página principal). Detalhe: ` +
          `${warmErr?.message || warmErr}`
      );
    }
  }

  try {
    const { snapshot } = await abrirPainelEmIframe({
      rota,
      painelId,
      capturarDom: false,
      timeoutMs: 60000,
    });
    const resolvido = {
      snap: snapshot,
      aviso: "Dados carregados via iframe oculto.",
    };
    cacheDoLote?.set(painelId, resolvido);
    return resolvido;
  } catch (e) {
    if (local) {
      const idadeMin = Math.max(1, Math.round((Date.now() - (local.ts || 0)) / 60000));
      return {
        snap: local,
        aviso:
          `Snapshot com ${idadeMin} min de idade — dados podem estar defasados ` +
          `(captura fresca via iframe falhou: ${e?.message || e}).`,
      };
    }
    throw new Error(
      `Falha ao carregar painel "${obterLabel(painelId)}" em iframe: ${
        e?.message || e
      }`
    );
  }
}

/**
 * Executa exportação tabular (PDF/Excel/CSV).
 *
 * @returns {Promise<{ status, arquivoNome?, erro?, aviso? }>}
 */
export async function executarFormatoTabular({
  painelId,
  formato,
  handle,
  subpasta,
  ocultarPrecos,
  warmupRequest,
  tema,
  densidade,
  cacheDoLote,
}) {
  let resolvido;
  try {
    resolvido = await resolverSnapshot(painelId, { warmupRequest, cacheDoLote });
  } catch (e) {
    return { status: "erro", erro: e?.message || String(e) };
  }

  if (!resolvido) {
    return {
      status: "pulado",
      erro: `Snapshot ausente — abra a página "${obterLabel(painelId)}" para que o agendador capture os dados.`,
    };
  }

  const { snap, aviso } = resolvido;
  const baseTitulo = snap.payload?.titulo || obterLabel(painelId);
  const { resumo: r0 = [], secoes: s0 = [] } = snap.payload || {};
  const payload = ocultarPrecos
    ? filtrarPrecos({ resumo: r0, secoes: s0 })
    : { resumo: r0, secoes: s0 };

  const opts = {
    titulo: baseTitulo,
    resumo: payload.resumo,
    secoes: payload.secoes,
    filtros: [],
    // Tema/densidade do usuário resolvidos pelo AgendamentoContext no
    // momento da execução — PDF acompanha light/dark + accent + densidade;
    // Excel/CSV ignoram os campos extras sem quebrar.
    tema,
    densidade,
  };

  try {
    // Dentro do try: getDirectoryHandle lança com permissão revogada ou
    // pasta removida no meio do lote — fora daqui, a exceção subia,
    // abortava os painéis restantes e apagava o histórico dos já gravados.
    const destino = await resolverHandleDestino(handle, subpasta);
    if (formato === "pdf") {
      const { blob, filename } = gerarBlobPDF(opts);
      const nome = await escreverArquivo(destino, filename, blob);
      return { status: "ok", arquivoNome: nome, aviso };
    }
    if (formato === "excel") {
      const { blob, filename } = gerarBlobExcel(opts);
      const nome = await escreverArquivo(destino, filename, blob);
      return { status: "ok", arquivoNome: nome, aviso };
    }
    if (formato === "csv") {
      const arquivos = gerarBlobsCSV(opts);
      const nomes = [];
      for (const { blob, filename } of arquivos) {
        const nome = await escreverArquivo(destino, filename, blob);
        nomes.push(nome);
      }
      return { status: "ok", arquivoNome: nomes.join(", "), aviso };
    }
    return { status: "erro", erro: `Formato desconhecido: ${formato}` };
  } catch (e) {
    return { status: "erro", erro: e?.message || "Falha ao gravar arquivo." };
  }
}

/**
 * Executa exportação de imagem (PNG/JPG) — SEMPRE via iframe oculto.
 *
 * Havia uma "estratégia 1" que capturava o DOM da aba principal quando o
 * painel estava montado nela. Ela foi removida (2026-08-21) porque:
 *  - mutava a aba VIVA do usuário: `setRenderAll(true)` + `data-export-mode`
 *    na tela em que a pessoa estava trabalhando, congelando-a por segundos
 *    às 8h da manhã, sem nenhum aviso;
 *  - os elementos eram resolvidos ANTES da captura; se o usuário navegasse
 *    no meio, fotografava nós já destacados do DOM;
 *  - dava escalas diferentes (2 na aba, 1.5 no iframe) e conteúdos
 *    diferentes (a aba carrega os filtros da sessão) para o MESMO
 *    agendamento, conforme onde a aba estivesse.
 * O iframe é mais lento, mas entrega o mesmo arquivo todo dia.
 */
export async function executarFormatoImagem({
  painelId,
  formato,
  handle,
  subpasta,
  ocultarPrecos,
  warmupRequest,
  tema,
}) {
  const titulo = obterLabel(painelId);
  const rota = obterRota(painelId);
  // A imagem é uma captura da tela: não há como remover só as colunas de
  // dinheiro. Avisar no histórico é obrigatório — silêncio aqui mandava
  // valores para pasta compartilhada com o gestor achando que ocultou.
  const avisoPrecos = ocultarPrecos
    ? "Imagem exportada COM valores — ocultar preços não se aplica a PNG/JPG."
    : null;
  let destino;
  try {
    destino = await resolverHandleDestino(handle, subpasta);
  } catch (e) {
    return {
      status: "erro",
      erro: `Falha ao acessar a subpasta de destino: ${e?.message || e}`,
    };
  }

  // Estratégia 2: iframe oculto com captura DOM completa.
  if (!rota) {
    return {
      status: "pulado",
      erro: `Sem rota cadastrada para "${titulo}".`,
    };
  }

  // Warm-up proativo (PJ2 — 2026-05-22): antes de criar o iframe, força
  // refresh do token na aba principal via GET /api/me. Resolve duas dores
  // que estavam quebrando 100% dos JPGs agendados:
  //   1. Cold start do Render free: 1ª request acorda o servidor; iframe
  //      depois já encontra backend warm.
  //   2. Race de refresh: o iframe e a aba principal compartilham o mesmo
  //      refreshToken em localStorage. Se ambos disparam refresh juntos,
  //      simplejwt invalida um. Fazendo refresh NA PARENT antes garante
  //      que o iframe lê o accessToken já novo e nem precisa refrescar.
  // silentError: true porque tratamos o erro inline (status erro com
  // mensagem clara).
  if (warmupRequest) {
    try {
      await warmupRequest("/api/me", { silentError: true });
    } catch (warmErr) {
      return {
        status: "erro",
        erro:
          "Não foi possível validar a sessão antes de capturar JPG/PNG. " +
          "Recarregue a página principal e refaça o login se necessário. " +
          `(detalhe: ${warmErr?.message || warmErr})`,
      };
    }
  }

  try {
    const { snapshot, canvases } = await abrirPainelEmIframe({
      rota,
      painelId,
      capturarDom: true,
      timeoutMs: 60000,
      // Fundo do tema do usuário (dark mode gerava imagem de fundo branco).
      backgroundColor: tema?.background,
    });
    if (!canvases || canvases.length === 0) {
      return {
        status: "pulado",
        erro: `Iframe carregou mas não capturou conteúdo de "${titulo}".`,
      };
    }

    const tituloFinal = snapshot.payload?.titulo || titulo;
    const subtitle = `${marcaDaExportacao()}  •  ${new Date().toLocaleString("pt-BR")}`;
    const combined = combineCanvasesVertical(canvases, {
      padding: 40,
      title: tituloFinal,
      subtitle,
      backgroundColor: tema?.background,
      titleColor: tema?.textPrimary,
      subtitleColor: tema?.textSecondary,
      accentColor: tema?.accent,
      fontFamily: tema?.fontFamily,
    });
    // O caminho do iframe nao tinha gate nenhum: com renderAll ligado, um
    // painel de ~800 linhas virava canvas de ~2100x40000, o Chrome desistia
    // de desenhar sem lancar, e o arquivo EM BRANCO era gravado como "ok".
    const inviavel = motivoImagemInviavel(combined);
    if (inviavel) {
      return { status: "erro", erro: inviavel };
    }
    const mime = formato === "jpg" ? "image/jpeg" : "image/png";
    const quality = formato === "jpg" ? 0.92 : undefined;
    const blob = await canvasToBlob(combined, mime, quality);
    const ext = formato === "jpg" ? "jpg" : "png";
    const filename = `${slugifyFilename(tituloFinal)}_${timestampForFilename()}.${ext}`;
    const nome = await escreverArquivo(destino, filename, blob);
    return {
      status: "ok",
      arquivoNome: nome,
      // Os dois avisos coexistem: o de captura e o de que a imagem saiu com
      // valores. Concatenar em vez de sobrescrever — o aviso de preços é o
      // que importa para quem ligou "ocultar preços", e este é o caminho
      // MAIS comum (painel que não está aberto na aba).
      aviso: ["Capturado via iframe oculto.", avisoPrecos]
        .filter(Boolean)
        .join(" "),
    };
  } catch (e) {
    return {
      status: "erro",
      erro: e?.message || "Falha ao capturar via iframe oculto.",
    };
  }
}

/**
 * Executa um agendamento completo: itera painéis × formatos.
 *
 * @param {Object} ctx
 * @param {FileSystemDirectoryHandle} ctx.handle

 * @param {(endpoint: string, options?: Object) => Promise<unknown>} [ctx.warmupRequest]
 *   Função `request` do AuthContext. Quando passada, o executor faz um
 *   GET `/api/me` antes de cada iframe oculto, garantindo refresh do
 *   token + acordando backend Render free. Sem isso, JPGs caíam 100%
 *   em "Iframe redirecionou para login" (PJ — 2026-05-22).
 * @param {Object} [ctx.tema]  tema do usuário resolvido em hex
 *   (montarTemaExportAtual) — PDF/PNG/JPG acompanham light/dark + accent.
 * @param {("compact"|"cozy"|"comfort")} [ctx.densidade]  densidade de
 *   tabelas das Configurações (lerDensidadeAtual) — reflete no PDF.
 */
export async function executarAgendamento(agendamento, ctx) {
  const { handle, warmupRequest, tema, densidade } = ctx;
  const resultados = [];
  // Vive só durante este lote: evita reabrir um iframe por formato para ler o
  // mesmo snapshot do mesmo painel (ver resolverSnapshot).
  const cacheDoLote = new Map();
  for (const painel of agendamento.paineis || []) {
    for (const formato of agendamento.formatos || []) {
      const item = { painelId: painel.painelId, formato };
      let res;
      try {
        if (formato === "png" || formato === "jpg") {
          res = await executarFormatoImagem({
            painelId: painel.painelId,
            formato,
            handle,
            subpasta: agendamento.subpasta,
            ocultarPrecos: agendamento.ocultarPrecos,
            warmupRequest,
            tema,
          });
        } else {
          res = await executarFormatoTabular({
            painelId: painel.painelId,
            formato,
            handle,
            subpasta: agendamento.subpasta,
            ocultarPrecos: agendamento.ocultarPrecos,
            warmupRequest,
            tema,
            densidade,
            cacheDoLote,
          });
        }
      } catch (e) {
        // Nenhum item pode derrubar o lote: os painéis seguintes ainda
        // rodam e os resultados já acumulados chegam ao histórico.
        res = { status: "erro", erro: e?.message || String(e) };
      }
      resultados.push({ ...item, ...res });
    }
  }
  return resultados;
}
